import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Container, Row, Col, Form, Button, Table, Alert, Spinner, Badge } from "react-bootstrap";
import { logout } from "./adminAuth";
import useInscriptos from "./useInscriptos";
import { exportInscriptosCsv } from "./exportCsv";
import { ADMIN_EVENTS, DEFAULT_ADMIN_EVENT_ID, getAdminEvent } from "../../config/adminEvents";
import { getStoredEventId, setStoredEventId } from "./adminEventStorage";
import { getUtmFromRecord } from "../../utils/utm";

const AdminPanel = () => {
  const history = useHistory();
  const [eventId, setEventId] = useState(() => getStoredEventId(DEFAULT_ADMIN_EVENT_ID));
  const evento = getAdminEvent(eventId);
  const isMetegol = evento.type === "metegol";

  const { inscriptos, loading, error, refetch } = useInscriptos(eventId);

  const [busqueda, setBusqueda] = useState("");
  const [fecha, setFecha] = useState("");
  const [sede, setSede] = useState("");
  const [marca, setMarca] = useState("");
  const [horario, setHorario] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");

  const marcas = useMemo(
    () => [...new Set(inscriptos.map((i) => i.marcaSeleccionada).filter(Boolean))].sort(),
    [inscriptos]
  );
  const horarios = useMemo(
    () => [...new Set(inscriptos.map((i) => i.horarioPreferencia).filter(Boolean))].sort(),
    [inscriptos]
  );
  const fechas = useMemo(
    () => [...new Set(inscriptos.map((i) => i.fechaTestDrive).filter(Boolean))].sort(),
    [inscriptos]
  );
  const ubicaciones = useMemo(
    () => [...new Set(inscriptos.map((i) => i.sede).filter(Boolean))].sort(),
    [inscriptos]
  );
  const sedes = useMemo(
    () => [...new Set(inscriptos.map((i) => i.sede || i.estadioPreferencia).filter(Boolean))].sort(),
    [inscriptos]
  );
  const utmSources = useMemo(
    () => [...new Set(inscriptos.map((i) => getUtmFromRecord(i).utm_source).filter(Boolean))].sort(),
    [inscriptos]
  );
  const utmCampaigns = useMemo(
    () => [...new Set(inscriptos.map((i) => getUtmFromRecord(i).utm_campaign).filter(Boolean))].sort(),
    [inscriptos]
  );

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return inscriptos.filter((i) => {
      const utm = getUtmFromRecord(i);

      if (utmSource && utm.utm_source !== utmSource) return false;
      if (utmCampaign && utm.utm_campaign !== utmCampaign) return false;

      if (isMetegol) {
        if (marca && i.marcaSeleccionada !== marca) return false;
        if (horario && i.horarioPreferencia !== horario) return false;
        if (sede && (i.sede || i.estadioPreferencia) !== sede) return false;
      } else {
        if (fecha && i.fechaTestDrive !== fecha) return false;
        if (sede && i.sede !== sede) return false;
      }

      if (!q) return true;
      const texto = [i.nombre, i.apellido, i.name, i.dni, i.phone, i.email, utm.utm_source, utm.utm_medium, utm.utm_campaign, utm.utm_term]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return texto.includes(q);
    });
  }, [inscriptos, busqueda, fecha, sede, marca, horario, utmSource, utmCampaign, isMetegol]);

  const handleEventChange = (id) => {
    setEventId(id);
    setStoredEventId(id);
    limpiarFiltros();
  };

  const handleLogout = () => {
    logout();
    history.push("/admin/login");
  };

  const limpiarFiltros = () => {
    setBusqueda("");
    setFecha("");
    setSede("");
    setMarca("");
    setHorario("");
    setUtmSource("");
    setUtmCampaign("");
  };

  const colCount = isMetegol ? 10 : 11;

  return (
    <div className="admin-page admin-panel">
      <Container fluid className="px-3 px-md-4 py-4">
        <div className="admin-panel__header d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="h4 mb-0">Inscriptos</h1>
            <p className="text-muted small mb-0">
              {loading ? "Cargando…" : `${filtrados.length} de ${inscriptos.length} registros — ${evento.label}`}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Form.Select
              size="sm"
              value={eventId}
              onChange={(e) => handleEventChange(e.target.value)}
              className="admin-event-select"
              style={{ minWidth: 200 }}
            >
              {ADMIN_EVENTS.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.label}
                </option>
              ))}
            </Form.Select>
            <Button variant="outline-secondary" size="sm" onClick={refetch} disabled={loading}>
              Actualizar
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => exportInscriptosCsv(filtrados, { eventLabel: evento.label, eventType: evento.type })}
              disabled={!filtrados.length}
            >
              Exportar CSV
            </Button>
            <Button variant="outline-dark" size="sm" onClick={handleLogout}>
              Salir
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <div className="admin-filtros card shadow-sm mb-4">
          <div className="card-body">
            <Row className="g-3">
              <Col md={4}>
                <Form.Label className="small fw-semibold">Buscar</Form.Label>
                <Form.Control
                  type="search"
                  placeholder={isMetegol ? "Nombre, DNI, teléfono…" : "Nombre, e-mail, DNI, teléfono…"}
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </Col>

              {isMetegol ? (
                <>
                  <Col md={2}>
                    <Form.Label className="small fw-semibold">Marca</Form.Label>
                    <Form.Select value={marca} onChange={(e) => setMarca(e.target.value)}>
                      <option value="">Todas</option>
                      {marcas.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small fw-semibold">Horario</Form.Label>
                    <Form.Select value={horario} onChange={(e) => setHorario(e.target.value)}>
                      <option value="">Todos</option>
                      {horarios.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small fw-semibold">Sede</Form.Label>
                    <Form.Select value={sede} onChange={(e) => setSede(e.target.value)}>
                      <option value="">Todas</option>
                      {sedes.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </>
              ) : (
                <>
                  <Col md={4}>
                    <Form.Label className="small fw-semibold">Fecha test drive</Form.Label>
                    <Form.Select value={fecha} onChange={(e) => setFecha(e.target.value)}>
                      <option value="">Todas</option>
                      {fechas.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small fw-semibold">Sucursal</Form.Label>
                    <Form.Select value={sede} onChange={(e) => setSede(e.target.value)}>
                      <option value="">Todas</option>
                      {ubicaciones.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </>
              )}

              <Col md={3}>
                <Form.Label className="small fw-semibold">UTM Source</Form.Label>
                <Form.Select value={utmSource} onChange={(e) => setUtmSource(e.target.value)}>
                  <option value="">Todas</option>
                  {utmSources.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-semibold">UTM Campaign</Form.Label>
                <Form.Select value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)}>
                  <option value="">Todas</option>
                  {utmCampaigns.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={1} className="d-flex align-items-end">
                <Button variant="link" className="text-decoration-none p-0" onClick={limpiarFiltros}>
                  Limpiar
                </Button>
              </Col>
            </Row>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <div className="admin-table-wrap card shadow-sm">
            <div className="table-responsive">
              <Table striped hover size="sm" className="admin-table mb-0">
                <thead>
                  <tr>
                    <th>Inscripción</th>
                    <th>Nombre</th>
                    {!isMetegol && <th>E-mail</th>}
                    <th>DNI</th>
                    <th>Teléfono</th>
                    {isMetegol ? (
                      <>
                        <th>Marca</th>
                        <th>Horario</th>
                        <th>Sede</th>
                      </>
                    ) : (
                      <>
                        <th>Fecha TD</th>
                        <th>Sucursal</th>
                        <th>Dirección</th>
                      </>
                    )}
                    <th>UTM Source</th>
                    <th>UTM Medium</th>
                    <th>UTM Campaign</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="text-center text-muted py-4">
                        No hay inscriptos con esos filtros.
                      </td>
                    </tr>
                  ) : isMetegol ? (
                    filtrados.map((i, idx) => {
                      const utm = getUtmFromRecord(i);
                      return (
                      <tr key={`${i.dni}-${i.fechaISO}-${idx}`}>
                        <td className="text-nowrap">{i.fecha || "—"}</td>
                        <td>
                          {i.nombre} {i.apellido}
                        </td>
                        <td>{i.dni}</td>
                        <td>{i.phone}</td>
                        <td>
                          <Badge bg={i.tipoFormulario === "fiat" ? "danger" : "secondary"}>
                            {i.marcaSeleccionada || "—"}
                          </Badge>
                        </td>
                        <td className="small">{i.horarioPreferencia || "—"}</td>
                        <td className="small">{i.sede || i.estadioPreferencia || "—"}</td>
                        <td className="small">{utm.utm_source || "—"}</td>
                        <td className="small">{utm.utm_medium || "—"}</td>
                        <td className="small">{utm.utm_campaign || "—"}</td>
                      </tr>
                      );
                    })
                  ) : (
                    filtrados.map((i, idx) => {
                      const utm = getUtmFromRecord(i);
                      return (
                      <tr key={`${i.dni}-${i.fechaISO}-${idx}`}>
                        <td className="text-nowrap">{i.fecha || "—"}</td>
                        <td>
                          {i.nombre} {i.apellido}
                        </td>
                        <td className="small">{i.email || "—"}</td>
                        <td>{i.dni}</td>
                        <td>{i.phone}</td>
                        <td className="small">{i.fechaTestDrive || "—"}</td>
                        <td className="small">{i.sede || "—"}</td>
                        <td className="small">{i.ubicacionTestDrive || "—"}</td>
                        <td className="small">{utm.utm_source || "—"}</td>
                        <td className="small">{utm.utm_medium || "—"}</td>
                        <td className="small">{utm.utm_campaign || "—"}</td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default AdminPanel;
